import { Checkbox } from "@/components/Checkbox/Checkbox";
import { DialogBox } from "@/components/DialogBox/DialogBox";
import { navigateToPrivacyPolicy, navigateToTermsOfService } from "@/components/TermsAndPolicyDialog/utils";
import { useUpdateUserApiV1UsersUserIdAcceptTermsPatchMutation } from "@/store/baseClientApi";
import { selectedTermsAccepted, setTermsAccepted } from "@/store/slices/termsAcceptedSlice";
import { RootState, store } from "@/store/store";
import { ensureError, localizeError } from "@/utils/helpers";
import { useAuth0 } from "@auth0/auth0-react";
import { Box, Link, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Trans, useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

export const TermsAndPolicyDialog = () => {
  const { t } = useTranslation();
  const { logout, user, isLoading, error } = useAuth0();
  const [acceptTerms] = useUpdateUserApiV1UsersUserIdAcceptTermsPatchMutation();
  const termsAccepted = useSelector((state: RootState) => selectedTermsAccepted(state)) ?? false;
  const [acceptedTerms, setAcceptedTerms] = useState<boolean>(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState<boolean>(false);

  const handleAcceptTerms = async () => {
    try {
      if (!user?.sub) {
        throw new Error("User ID is missing");
      }

      await acceptTerms({ userId: user.sub }).unwrap();
      store.dispatch(setTermsAccepted(true));

      window.location.reload();
    } catch (err) {
      const error = ensureError(err);
      toast.error(localizeError(t, error));
    }
  };

  useEffect(() => {
    if (error !== undefined) {
      const ensuredErr = ensureError(error);
      toast.error(localizeError(t, ensuredErr));
    }
  }, [error, t]);

  return (
    <DialogBox
      title={t("common:title:termsAndPolicy")}
      sx={{ ".MuiPaper-root": { width: "100%", maxWidth: "800px", margin: "16px" } }}
      message={
        <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <Typography>
            <Trans
              t={t}
              components={{
                terms: <Link component={"button"} onClick={navigateToTermsOfService} />,
                privacy: <Link component={"button"} onClick={navigateToPrivacyPolicy} />
              }}
              i18nKey={"message.reviewAndAgreeToPolicies"}
            />
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-start", alignItems: "center" }}>
            <Checkbox value={acceptedTerms} onChange={(_, checked) => setAcceptedTerms(checked)} />
            <Typography>
              <Trans
                components={{
                  a: <Link component={"button"} onClick={navigateToTermsOfService} />
                }}
                t={t}
                i18nKey={"message.acceptTermsOfService"}
              />
            </Typography>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "flex-start", alignItems: "center" }}>
            <Checkbox value={acceptedPrivacy} onChange={(_, checked) => setAcceptedPrivacy(checked)} />
            <Typography>
              <Trans
                components={{
                  a: <Link component={"button"} onClick={navigateToPrivacyPolicy} />
                }}
                t={t}
                i18nKey={"message.acceptPrivacyPolicy"}
              />
            </Typography>
          </Box>
        </Box>
      }
      onClose={async (approve) => {
        if (!approve || !acceptedPrivacy || !acceptedTerms) {
          // User must be logged out
          await logout({
            logoutParams: { returnTo: window.location.origin }
          });
          return;
        }

        handleAcceptTerms();
      }}
      open={!termsAccepted}
      setOpen={() => {
        return;
      }}
      args={undefined}
      hideDecline
      hideClose
      confirmText={t("common:button.accept")}
      confirmDisable={isLoading || error !== undefined || !acceptedPrivacy || !acceptedTerms}
    />
  );
};
